/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { msdyn365Commerce } from '@msdyn365-commerce/core';

import { clearSelectedStoreId, clearTableId, getGlobantSuiteId, getSelectedStoreId, getTableId } from './cookieutils';

const SUITE_CONTEXT_POLL_INTERVAL_MS = 250;
const SUITE_CONTEXT_TIMEOUT_MS = 20000;

/**
 * Timeout for callers that run behind a user gesture. The suite has normally resolved long before the user
 * can click, so this only covers a click that lands while the header call is still in flight and keeps a
 * failed validation from holding the cart spinner for the full default window.
 */
export const INTERACTIVE_SUITE_CONTEXT_TIMEOUT_MS = 5000;

export interface ISuiteContext {
    channelId: string;
    tableId: string;
}

/**
 * Drops a resolved suite context that belongs to a different suite than the one now being opened. Both
 * cookies are session cookies on path=/ and survive navigation, so without this a load for suite B would
 * satisfy every readiness check below with suite A's hospitality id until the header finishes re-resolving.
 *
 * Only the header ever writes a value into tableId; this clears it so a stale one cannot be mistaken for a
 * resolved suite.
 */
export const invalidateSuiteContextIfSuiteChanged = (suiteId: string | null): void => {
    if (!suiteId || suiteId === getGlobantSuiteId()) {
        return;
    }

    console.log('LTS Info', 'suitecontext: suite changed, clearing the resolved context', {
        previousSuiteId: getGlobantSuiteId(),
        suiteId
    });

    clearTableId();
    clearSelectedStoreId();
};

const readSuiteContext = (): ISuiteContext | undefined => {
    const channelId = getSelectedStoreId();
    const tableId = getTableId();

    return channelId && tableId ? { channelId, tableId } : undefined;
};

/**
 * Resolves once the header view has written both the channelid and tableId cookies, which it does only after
 * LAC_SuiteToValidateAdmin has translated the external suite id into the hospitality id. Callers that send
 * TableAssigned must wait for this rather than reading the cookies on mount, because the header resolves the
 * suite over the network and the cookies are empty until it returns.
 *
 * Resolves undefined when the suite never resolves inside the bounded window.
 */
export const waitForSuiteContext = async (timeoutMs: number = SUITE_CONTEXT_TIMEOUT_MS): Promise<ISuiteContext | undefined> => {
    const immediate = readSuiteContext();
    if (immediate || !msdyn365Commerce.isBrowser) {
        return immediate;
    }

    return new Promise<ISuiteContext | undefined>(resolve => {
        const deadline = Date.now() + timeoutMs;
        const timer = window.setInterval(() => {
            const resolved = readSuiteContext();
            if (resolved) {
                window.clearInterval(timer);
                resolve(resolved);
                return;
            }

            if (Date.now() > deadline) {
                window.clearInterval(timer);
                resolve(undefined);
            }
        }, SUITE_CONTEXT_POLL_INTERVAL_MS);
    });
};
